import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { FinancRp } from '../interfaces/financ-rp';
import { Observable } from 'rxjs';
import { PageResult } from '../interfaces/page-result';
import { UnMedida } from '../interfaces/un-medida';
import { JwtAuthService } from './auth/jwt-auth.service';
import { Cadastro } from '../interfaces/cadastro';
import { FinancRpLctoBaixa } from '../interfaces/financ-rp-lcto-baixa';
import { TipoCobranca } from '../interfaces/tipo-cobranca';
import { FinancRpCl } from '../interfaces/financ-rp-cl';

@Injectable({
  providedIn: "root",
})
export class FinancRpService {
  readonly path = "/financRp";

  constructor(
    private http: HttpClient,
    public jwtAuthService: JwtAuthService
  ) {}

  saveOrUpdate(financRp: FinancRp): Observable<FinancRp> {
    return this.http.put<FinancRp>(
      `${this.jwtAuthService.apiUrl + this.path}`,
      financRp
    );
  }

  saveOrUpdateAndRecalc(financRp: FinancRp): Observable<FinancRp[]> {
    return this.http.put<FinancRp[]>(
      `${this.jwtAuthService.apiUrl + this.path}/recalc`,
      financRp
    );
  }

  getParcelas(fatura: number): Observable<FinancRp[]> {
    return this.http.get<FinancRp[]>(
      `${this.jwtAuthService.apiUrl + this.path}/lista/${fatura}`
    );
  }

  getItem(id: number): Observable<FinancRp> {
    return this.http.get<FinancRp>(
      `${this.jwtAuthService.apiUrl + this.path}/${id}`
    );
  }

  getItemPage(
    page?: number,
    size?: number,
    order?: string,
    direction?: string,
    natureza = "CREDITO",
    cadastro?: Cadastro,
    comRemessa = true,
    semRemessa = true,
    selectedSearch = "TODOS",
    dataInicial?: Date,
    dataFinal?: Date,
    tipo?: TipoCobranca
  ): Observable<PageResult<FinancRp>> {
    let url =
      `${this.jwtAuthService.apiUrl + this.path}/page/get/?page=${page}&size=` +
      `${size}&order=${order}&direction=${direction}&natureza=${natureza}&cadastroId=${
        cadastro?.id ?? -1
      }` +
      `&comRemessa=${comRemessa}&semRemessa=${semRemessa}` +
      `&selectedSearch=${selectedSearch}&dataInicial=${dataInicial?.toISOString()}&dataFinal=${dataFinal?.toISOString()}` +
      `&tipoCobrancaId=${tipo?.id ?? -1}`;

    return this.http.get<PageResult<FinancRp>>(url);
  }

  getItemPageSearch(
    page?: number,
    size?: number,
    search?: string,
    order?: string,
    direction?: string,
    natureza = "CREDITO",
    cadastro?: Cadastro,
    comRemessa = true,
    semRemessa = true,
    selectedSearch = "TODOS",
    dataInicial?: Date,
    dataFinal?: Date,
    tipo?: TipoCobranca
  ): Observable<PageResult<FinancRp>> {
    return this.http.get<PageResult<FinancRp>>(
      `${this.jwtAuthService.apiUrl + this.path}/page/get/?page=${page}&size=` +
        `${size}&search=${search}&order=${order}&direction=${direction}&natureza=${natureza}&cadastroId=${
          cadastro?.id ?? -1
        }` +
        `&comRemessa=${comRemessa}&semRemessa=${semRemessa}` +
        `&selectedSearch=${selectedSearch}&dataInicial=${dataInicial?.toISOString()}&dataFinal=${dataFinal?.toISOString()}` +
        `&tipoCobrancaId=${tipo?.id ?? -1}`
    );
  }

  // tipo = ENCONTRO_CONTAS , EXTRATO
  printExtratoOrEncontro(
    cadastroId: number,
    natureza = "R",
    tipo = "EXTRATO"
  ): Observable<any> {
    //ENCONTRO_CONTAS
    return this.http.get(
      `${this.jwtAuthService.apiUrl + this.path}/cadastro/print/${
        cadastroId ?? 0
      }?natureza=${natureza}&tipo=${tipo}`,
      { responseType: "arraybuffer" }
    );
  }

  printPreRecibos(rps: FinancRp[]): Observable<any> {
    return this.http.get(
      `${this.jwtAuthService.apiUrl + this.path}/print/prerecibo?ids=${rps
        .map((rp) => rp.id + "")
        .join(",")}`,
      { responseType: "arraybuffer" }
    );
  }

  printCarnePix(rps: FinancRp[], financCCId: number): Observable<any> {
    const ids = rps.map((rp) => rp.id + '').join(',');
    return this.http.get(
      `${this.jwtAuthService.apiUrl + this.path}/print/carnepix?ids=${ids}&financCCId=${financCCId}`,
      { responseType: 'arraybuffer' }
    );
  }

  gerarRemessaBoleto(rps: FinancRp[], financCCId: number): Observable<any> {
    return this.http.post(
      `${this.jwtAuthService.apiUrl}/financ/transm?rps=${rps
        .map((rp) => rp.id + "")
        .join(",")}&financCCId=${financCCId}`,
      null
    );
  }

  printDuplicatas(rps: Number[]): Observable<any> {
    return this.http.get(
      `${
        this.jwtAuthService.apiUrl + this.path
      }/print/duplicatas?controles=${rps.join(",")}`,
      { responseType: "arraybuffer" }
    );
  }

  saveOrUpdateClasse(financRpCl: FinancRpCl): Observable<FinancRpCl> {
    return this.http.put<FinancRpCl>(`${this.jwtAuthService.apiUrl + this.path}/classe`,financRpCl);
  }

  deleteClasse(faturaId: number, classeId: number): Observable<any> {
    return this.http.delete<any>(`${this.jwtAuthService.apiUrl + this.path}/classe/${faturaId}/${classeId}`);
  }

  getClassesByFaturaIdPage(fatura: number, page: number = 1, size: number = 20): Observable<PageResult<FinancRpCl>> {
    return this.http.get<PageResult<FinancRpCl>>(
      `${this.jwtAuthService.apiUrl + this.path}/classe/page/${fatura}?page=${page}&size=${size}`
    );
  }

  getAdiantamentos(cadastroId: number): Observable<FinancRp[]> {
    return this.http.get<FinancRp[]>(
      `${this.jwtAuthService.apiUrl + this.path}/adiantamentos/${cadastroId}`
    );
  }

  delete(id: number): Observable<any> {
    return this.http.delete<any>(
      `${this.jwtAuthService.apiUrl + this.path}/${id}`
    );
  }

}
